/**
 * 내 브랜드 — 소속·역할·초대 (261013-account-brand-membership). 261010-agency-console 의 대행사 콘솔을 대신한다.
 *
 * 계정 – 브랜드(소속·역할) – 구독. 한 계정이 브랜드 여럿에 소속되고, 소속마다 역할이 있다.
 *
 *   | 할 수 있는 일                                    | 소유자 | 관리자 |
 *   | 보고서·기사 검토·구독 시작·카드 교체(자기 카드)    |   ✓    |   ✓    |
 *   | 다른 계정 초대·내보내기·역할 바꾸기·소유자 넘기기 |   ✓    |        |
 *   | 승인 전 브랜드 삭제                              |   ✓    |        |
 *   | 내 소속에서 나가기                               |   ✓    |   ✓    |
 *
 * ★ **마지막 소유자는 나갈 수 없다**(409) — 다른 소속자를 소유자로 올린 뒤에 나간다. 주인 없는 브랜드를 만들지 않는다.
 * ★ 브랜드명·프로필·질문은 **운영자 영역**이다 — 이 파일에 그 쓰기를 두지 않는다. 사용자는 링크만 고친다.
 * ★ 범위는 **내 소속**이다(승인 대기·결제 대기 포함) — 리포트 범위(`brandIds`, 승인된 것만)와 다르다. 범위 밖은 404.
 * ★ 초대 주소는 **메일로만** 간다 — 발급 응답에 싣지 않는다(초대한 사람이 남의 계정 비밀번호를 정하게 된다)
 */
import { z } from 'zod';
import { BRAND_STATUSES } from '../brand-status.ts';
import { IdParams, IsoDateTime, Ok } from '../common.ts';
import { defineRoute } from '../route.ts';
import { BRAND_ROLES, Password } from './auth.ts';
import { AssetLinks, AssetUrl, ExtraLinks } from './registry.ts';
import { BrandName, DesiredQuery } from './signup.ts';

/** 초대 수명 — 7일 */
export const INVITE_DAYS = 7;

/** 초대 토큰 — base64url */
export const InviteToken = z.string().min(20).max(200);

/**
 * 브랜드를 만드는 본문 — 가입의 첫 브랜드와 같은 칸이다(업체명은 계정에 이미 있어 받지 않는다).
 * 브랜드명은 표시 이름(`brand.name`)이 되고, 판정용 목표 브랜드명은 운영자가 승인 때 정한다 (261013-signup-company-name-to-account).
 * 홈페이지 필수 · 네이버 플레이스 권장 · 그 밖의 링크 자유(`AssetLinks`)
 */
export const NewBrandBody = z
  .object({
    brandName: BrandName,
    ...AssetLinks,
    desiredQuery: DesiredQuery,
    note: z.string().trim().max(2000).default(''),
  })
  .strict();

export const MyBrand = z.object({
  id: z.uuid(),
  name: z.string(),
  status: z.enum(BRAND_STATUSES),
  /** 이 브랜드에서 내 역할 */
  role: z.enum(BRAND_ROLES),
  homepageUrl: z.string().nullable(),
  naverPlaceUrl: z.string().nullable(),
  links: z.array(z.string()),
  /** 운영자가 정한다 — 읽기만 */
  brandNames: z.array(z.string()),
  queryCount: z.number().int(),
  memberCount: z.number().int(),
  /** 승인 전 요청 내용 */
  desiredQueries: z.array(z.string()),
  note: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type MyBrand = z.infer<typeof MyBrand>;

export const listMyBrands = defineRoute({
  method: 'get',
  path: '/api/account/brands',
  access: 'member',
  summary: '내 브랜드 — 역할·상태·소속자 수. 승인 대기·결제 대기 포함',
  response: z.object({ brands: z.array(MyBrand) }),
});

/**
 * 브랜드 추가 — 승인 대기로 들어가고 만든 계정이 소유자다. 운영자가 브랜드명·질문을 확정해 승인하면 측정이 시작된다
 * (가입과 같은 승인 흐름)
 */
export const addMyBrand = defineRoute({
  method: 'post',
  path: '/api/account/brands',
  access: 'member',
  summary: '브랜드 추가 — 승인 대기. 만든 계정이 소유자',
  body: NewBrandBody,
  response: z.object({ brand: MyBrand }),
});

export const getMyBrand = defineRoute({
  method: 'get',
  path: '/api/account/brands/{id}',
  access: 'member',
  summary: '내 브랜드 한 곳',
  params: IdParams,
  response: z.object({ brand: MyBrand }),
});

export const updateMyBrand = defineRoute({
  method: 'patch',
  path: '/api/account/brands/{id}',
  access: 'member',
  summary: '브랜드 링크 수정 — 업체명·홈페이지·플레이스·그 밖의 링크만. 브랜드명·프로필·질문은 운영자가 한다',
  params: IdParams,
  body: z
    .object({
      name: z.string().trim().min(1).max(120).optional(),
      /** 홈페이지는 필수라 지울 수 없다(null 을 받지 않는다) — 바꾸기만 한다 */
      homepageUrl: AssetUrl.optional(),
      naverPlaceUrl: AssetUrl.nullable().optional(),
      links: ExtraLinks.optional(),
    })
    .strict(),
  response: z.object({ brand: MyBrand }),
});

export const deleteMyBrand = defineRoute({
  method: 'delete',
  path: '/api/account/brands/{id}',
  access: 'member',
  summary: '브랜드 삭제(soft) — 소유자만, 승인 전(pending) 브랜드만. 승인된 브랜드는 운영팀에 요청한다',
  params: IdParams,
  response: Ok,
});

// ── 소속자 ───────────────────────────────────────────────────────────────

export const BrandMember = z.object({
  userId: z.uuid(),
  email: z.string(),
  name: z.string().nullable(),
  companyName: z.string().nullable(),
  role: z.enum(BRAND_ROLES),
  isMe: z.boolean(),
  since: IsoDateTime,
});

export const listBrandMembers = defineRoute({
  method: 'get',
  path: '/api/account/brands/{id}/members',
  access: 'member',
  summary: '브랜드 소속자',
  params: IdParams,
  response: z.object({ members: z.array(BrandMember) }),
});

const MemberParams = z.object({ id: z.uuid(), userId: z.uuid() });

export const updateBrandMember = defineRoute({
  method: 'patch',
  path: '/api/account/brands/{id}/members/{userId}',
  access: 'member',
  summary: '역할 바꾸기 — 소유자만. 마지막 소유자를 관리자로 내리면 409(소유자 넘기기는 상대를 owner 로 올린다)',
  params: MemberParams,
  body: z.object({ role: z.enum(BRAND_ROLES) }).strict(),
  response: Ok,
});

export const removeBrandMember = defineRoute({
  method: 'delete',
  path: '/api/account/brands/{id}/members/{userId}',
  access: 'member',
  summary: '내보내기(소유자만)·나가기(나 자신 — 누구나). 마지막 소유자면 409',
  params: MemberParams,
  response: Ok,
});

// ── 초대 ─────────────────────────────────────────────────────────────────

export const BrandInvite = z.object({
  id: z.uuid(),
  email: z.string(),
  role: z.enum(BRAND_ROLES),
  state: z.enum(['open', 'used', 'expired', 'revoked']),
  expiresAt: IsoDateTime,
  usedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type BrandInvite = z.infer<typeof BrandInvite>;

export const listBrandInvites = defineRoute({
  method: 'get',
  path: '/api/account/brands/{id}/invites',
  access: 'member',
  summary: '초대 목록 — 소유자만, 최근 90일',
  params: IdParams,
  response: z.object({ invites: z.array(BrandInvite) }),
});

export const createBrandInvite = defineRoute({
  method: 'post',
  path: '/api/account/brands/{id}/invites',
  access: 'member',
  summary: '초대 — 소유자만. 메일로 보낸다(주소를 응답에 싣지 않는다). 같은 주소의 열린 초대는 회수된다',
  params: IdParams,
  body: z.object({ email: z.email(), role: z.enum(BRAND_ROLES).default('manager') }).strict(),
  response: z.object({ invite: BrandInvite }),
});

export const revokeBrandInvite = defineRoute({
  method: 'delete',
  path: '/api/account/brands/{id}/invites/{inviteId}',
  access: 'member',
  summary: '초대 회수 — 소유자만',
  params: z.object({ id: z.uuid(), inviteId: z.uuid() }),
  response: Ok,
});

// ── 초대 받는 쪽 ─────────────────────────────────────────────────────────

/**
 * 초대 확인 — 수락 화면이 무엇을 보여 줄지 정한다. **토큰을 경로에 싣지 않는다**(접근 로그에 남는다).
 * 무효 토큰은 400 하나로 답한다 — 만료·회수·사용됨을 가르지 않는다(토큰 추측에 정보를 주지 않는다)
 */
export const inspectInvite = defineRoute({
  method: 'post',
  path: '/api/invites/inspect',
  access: 'public',
  summary: '초대 내용 확인',
  body: z.object({ token: InviteToken }).strict(),
  response: z.object({
    brandName: z.string(),
    role: z.enum(BRAND_ROLES),
    /** 초대한 사람(이름·회사명 중 있는 것) — 메일을 받은 사람이 누가 불렀는지 알아보게 */
    inviterName: z.string().nullable(),
    maskedEmail: z.string(),
    /** 그 주소의 계정이 이미 있으면 새 비밀번호가 아니라 **기존 비밀번호**를 받는다 */
    accountExists: z.boolean(),
  }),
});

export const acceptInvite = defineRoute({
  method: 'post',
  path: '/api/invites/accept',
  access: 'public',
  summary:
    '초대 수락 — 계정이 없으면 비밀번호를 정해 만들고, 있으면 기존 비밀번호로 소속만 더한다. 메일을 받은 것이 이메일 인증이다',
  body: z.discriminatedUnion('existing', [
    z
      .object({
        existing: z.literal(false),
        token: InviteToken,
        password: Password,
        name: z.string().trim().min(1).max(60),
        agreeTerms: z.literal(true),
        agreePrivacy: z.literal(true),
        agreeMarketing: z.boolean().default(false),
      })
      .strict(),
    z.object({ existing: z.literal(true), token: InviteToken, password: z.string().min(1).max(200) }).strict(),
  ]),
  response: Ok,
});
